import { useCallback, useState } from "react";
import { Button, Segmented, Typography, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import DeliverySharePanel from "../../components/delivery/DeliverySharePanel";
import DeliveryFormFields from "../../components/delivery/DeliveryFormFields";
import copyToClipboard from "../../utils/copy";
import { buildDeliveryUrl, encodePayload } from "../../utils/deliveryToken";
import { createDelivery } from "../../utils/deliveryAdminApi";
import { emptyDeliveryFormValues, validateDeliveryForm } from "../../utils/deliveryForm";
import { useTranslation } from "../../i18n";
import "./delivery.css";

const { Title, Paragraph } = Typography;

function DeliveryAdmin() {
    const { t } = useTranslation();
    const [values, setValues] = useState(emptyDeliveryFormValues);
    const [storageMode, setStorageMode] = useState("short");
    const [generatedUrl, setGeneratedUrl] = useState("");
    const [generatedAlbumName, setGeneratedAlbumName] = useState("");
    const [expiryPreview, setExpiryPreview] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    const handleField = useCallback((field, value) => {
        setValues((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleGenerate = useCallback(async () => {
        setError("");
        setGeneratedUrl("");
        setGeneratedAlbumName("");
        setExpiryPreview("");

        let normalized;
        try {
            normalized = validateDeliveryForm(values, t);
        } catch (err) {
            setError(err.message || t("delivery.admin.generateFailed"));
            return;
        }

        const showResult = (url) => {
            setGeneratedUrl(url);
            setGeneratedAlbumName(normalized.albumName);
            setExpiryPreview(values.expiry.format("YYYY-MM-DD HH:mm"));
        };

        // 自包含链接（老式）：不落库，所有信息 base64 编码进链接。
        if (storageMode === "inline") {
            const token = encodePayload({
                t: normalized.type,
                i: normalized.albumPath,
                e: normalized.expiry,
                n: normalized.albumName,
                c: normalized.consent,
                downloadAll: normalized.downloadAllType
                    ? { type: normalized.downloadAllType, value: normalized.downloadAllToken }
                    : null,
            });
            showResult(buildDeliveryUrl(token));
            return;
        }

        // 短链：详情落 D1，链接只含随机短 ID。
        setGenerating(true);
        try {
            const created = await createDelivery({
                albumPath: normalized.albumPath,
                albumName: normalized.albumName,
                expiry: normalized.expiry,
                consent: normalized.consent,
                type: normalized.type,
                downloadAllType: normalized.downloadAllType,
                downloadAllToken: normalized.downloadAllToken,
                archiveMarkdown: normalized.archiveMarkdown,
            });
            showResult(buildDeliveryUrl(created.id));
        } catch (err) {
            setError(err.message || t("delivery.admin.generateFailed"));
        } finally {
            setGenerating(false);
        }
    }, [storageMode, values, t]);

    const handleCopy = useCallback(async () => {
        if (!generatedUrl) {
            return;
        }
        try {
            await copyToClipboard(generatedUrl);
            message.success(t("delivery.admin.linkCopied"));
        } catch {
            message.error(t("delivery.admin.copyFailed"));
        }
    }, [generatedUrl, t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("delivery.admin.title")}</span>
                </div>
                <Paragraph style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                    {t("delivery.admin.subtitle")}
                </Paragraph>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("delivery.admin.description")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <div className="delivery-admin-field">
                        <label>{t("delivery.admin.storageModeLabel")}</label>
                        <Segmented
                            block
                            value={storageMode}
                            onChange={setStorageMode}
                            options={[
                                { label: t("delivery.admin.storageShort"), value: "short" },
                                { label: t("delivery.admin.storageInline"), value: "inline" },
                            ]}
                        />
                        <Paragraph
                            type="secondary"
                            style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                        >
                            {storageMode === "short"
                                ? t("delivery.admin.storageShortHint")
                                : t("delivery.admin.storageInlineHint")}
                        </Paragraph>
                    </div>

                    <DeliveryFormFields values={values} onChange={handleField} />

                    {error && (
                        <Paragraph type="danger" style={{ marginBottom: "1rem" }}>
                            {error}
                        </Paragraph>
                    )}

                    <div className="delivery-admin-actions">
                        <Button type="primary" loading={generating} onClick={handleGenerate}>
                            {t("delivery.admin.generate")}
                        </Button>
                    </div>

                    {generatedUrl && (
                        <div className="delivery-admin-result">
                            <Title level={5}>{t("delivery.admin.resultTitle")}</Title>
                            {expiryPreview && (
                                <p className="delivery-admin-preview">
                                    {t("delivery.admin.expiresAt", { time: expiryPreview })}
                                </p>
                            )}
                            <div className="delivery-admin-result__url">{generatedUrl}</div>
                            <div className="delivery-admin-actions">
                                <Button type="primary" onClick={handleCopy}>
                                    {t("delivery.admin.copyLink")}
                                </Button>
                            </div>

                            <div className="delivery-admin-poster">
                                <Title level={5}>{t("delivery.admin.posterTitle")}</Title>
                                <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                                    {t("delivery.admin.posterHint")}
                                </Paragraph>
                                <DeliverySharePanel
                                    url={generatedUrl}
                                    albumName={generatedAlbumName}
                                    expiresAtLabel={expiryPreview}
                                    compact
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default DeliveryAdmin;
